
import React, { useState } from 'react';
import { FileText, Download, Trash2, AlertCircle, FileImage, FileVideo, FileAudio, FileArchive } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface FileImport {
  id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  storage_path?: string;
  connection_id: string;
  created_at: string;
}

interface FileItemProps {
  file: FileImport;
  onDelete: () => void;
}

export const FileItem: React.FC<FileItemProps> = ({ file, onDelete }) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [imageError, setImageError] = useState(false);
  const { toast } = useToast();

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isImageFile = (fileName: string): boolean => {
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];
    const extension = fileName.split('.').pop()?.toLowerCase();
    return imageExtensions.includes(extension || '');
  };

  const getPublicUrl = (storagePath: string): string => {
    const { data } = supabase.storage
      .from('company-files')
      .getPublicUrl(storagePath);
    return data.publicUrl;
  };

  const handleDownload = async () => {
    if (!file.storage_path) {
      toast({
        title: 'Error',
        description: 'File not available for download',
        variant: 'destructive',
      });
      return;
    }

    try {
      // Try to get a signed URL for better security
      const { data: signedData, error: signedError } = await supabase.storage
        .from('company-files')
        .createSignedUrl(file.storage_path, 3600); // 1 hour expiry

      let downloadUrl: string;
      
      if (signedError || !signedData?.signedUrl) {
        // Fallback to public URL
        downloadUrl = getPublicUrl(file.storage_path);
      } else {
        downloadUrl = signedData.signedUrl;
      }

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = file.file_name;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: 'Error',
        description: 'Failed to download file',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      // Delete from storage if path exists
      if (file.storage_path) {
        const { error: storageError } = await supabase.storage
          .from('company-files')
          .remove([file.storage_path]);
        
        if (storageError) {
          console.error('Storage deletion error:', storageError);
        }
      }

      onDelete();
    } catch (error) {
      console.error('Delete error:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete file',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const getFileIcon = (fileType: string, fileName: string) => {
    const extension = fileName.split('.').pop()?.toLowerCase() || '';
    
    if (isImageFile(fileName)) {
      return <FileImage className="h-8 w-8 text-blue-500" />;
    }
    
    if (['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm'].includes(extension)) {
      return <FileVideo className="h-8 w-8 text-purple-500" />;
    }
    
    if (['mp3', 'wav', 'ogg', 'aac', 'flac'].includes(extension)) {
      return <FileAudio className="h-8 w-8 text-green-500" />;
    }
    
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)) {
      return <FileArchive className="h-8 w-8 text-orange-500" />;
    }
    
    return <FileText className="h-8 w-8 text-blue-500" />;
  };

  const renderPreview = () => {
    if (!file.storage_path || !isImageFile(file.file_name) || imageError) {
      return getFileIcon(file.file_type, file.file_name);
    }

    const imageUrl = getPublicUrl(file.storage_path);
    
    return (
      <div className="w-16 h-16 rounded overflow-hidden bg-gray-100 flex items-center justify-center">
        <img
          src={imageUrl}
          alt={file.file_name}
          className="w-full h-full object-cover"
          onError={() => setImageError(true)}
          onLoad={() => setImageError(false)}
        />
      </div>
    );
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className="flex items-start space-x-3 flex-1">
          {renderPreview()}
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-medium text-gray-900 truncate">
              {file.file_name}
            </h3>
            <div className="mt-1 flex items-center space-x-4 text-xs text-gray-500">
              <span className="bg-gray-100 px-2 py-1 rounded">
                {file.file_type}
              </span>
              <span>{formatFileSize(file.file_size)}</span>
              <span>{formatDate(file.created_at)}</span>
            </div>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {file.storage_path && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDownload}
              title="Download file"
            >
              <Download className="h-4 w-4" />
            </Button>
          )}
          
          {!showDeleteConfirm ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowDeleteConfirm(true)}
              title="Delete file"
            >
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>
          ) : (
            <div className="flex items-center space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
